import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react'
import { CoverImage } from '../../ui/CoverImage'

/**
 * SPEC-016 Album Studio — shared gray-on-white / warm-gold primitives (ADR-084).
 * Every color is a `--as-*` token so the studio's palette lives in one CSS scope
 * (`.album-studio`) and the WCAG AA guarantees hold. Kept together so the list,
 * overview, and shell share one visual language without repeating class strings.
 */

type BtnVariant = 'primary' | 'secondary' | 'ghost' | 'subtle' | 'danger'
type BtnSize = 'sm' | 'md'

const BTN_VARIANT: Record<BtnVariant, string> = {
  primary: 'bg-[var(--as-gold)] text-white shadow-[var(--as-shadow-sm)] hover:bg-[var(--as-gold-600)]',
  secondary: 'bg-[var(--as-panel)] text-[var(--as-ink)] border border-[var(--as-line)] shadow-[var(--as-shadow-sm)] hover:border-[#d9cfbc]',
  ghost: 'text-[var(--as-ink-2)] hover:bg-black/[0.045]',
  subtle: 'bg-[var(--as-gold-tint-2)] text-[var(--as-gold-600)] hover:bg-[var(--as-gold-tint)]',
  danger: 'text-[var(--as-rose)] hover:bg-[var(--as-rose-tint)]',
}

interface BtnProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: BtnVariant
  size?: BtnSize
}

/**
 * The studio's button. Focus-visible ring is gold; disabled buttons dim and stop
 * pointer events but remain in the DOM for context.
 *
 * @param variant - Visual weight (primary gold / secondary / ghost / subtle / danger).
 * @param size - `sm` for dense table rows, `md` for headers.
 */
export function Btn({ variant = 'primary', size = 'md', className = '', type = 'button', ...rest }: BtnProps): JSX.Element {
  const sizing = size === 'sm' ? 'text-[12.5px] px-[11px] py-[6px] gap-1.5' : 'text-sm px-[15px] py-[9px] gap-[7px]'
  return (
    <button
      type={type}
      className={`inline-flex items-center justify-center whitespace-nowrap rounded-[9px] border border-transparent font-semibold leading-none transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--as-gold)] focus-visible:ring-offset-1 focus-visible:ring-offset-[var(--as-paper)] disabled:cursor-not-allowed disabled:opacity-45 disabled:shadow-none ${sizing} ${BTN_VARIANT[variant]} ${className}`}
      {...rest}
    />
  )
}

/** A letter-spaced uppercase micro-label in deep gold (the studio eyebrow). */
export function Eyebrow({ children, className = '' }: { children: ReactNode; className?: string }): JSX.Element {
  return (
    <div className={`text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--as-gold-600)] ${className}`}>
      {children}
    </div>
  )
}

/** A monospace keycap chip (e.g. the ⌘ / K shortcut hints). */
export function Kbd({ children }: { children: ReactNode }): JSX.Element {
  return (
    <kbd className="rounded-[5px] border border-b-2 border-[var(--as-line)] bg-[var(--as-panel)] px-1.5 py-px font-[family-name:var(--as-font-mono)] text-[11px] font-medium leading-normal text-[var(--as-ink-2)]">
      {children}
    </kbd>
  )
}

/** A rounded panel card with a hairline warm border and soft shadow. */
export function Panel({
  children,
  className = '',
  pad = true,
}: {
  children: ReactNode
  className?: string
  /** When false, the panel has no internal padding (for edge-to-edge content). */
  pad?: boolean
}): JSX.Element {
  return (
    <div
      className={`rounded-2xl border border-[var(--as-line)] bg-[var(--as-panel)] shadow-[var(--as-shadow-md)] ${pad ? 'p-6' : ''} ${className}`}
    >
      {children}
    </div>
  )
}

/** A small round color dot (status indicator; never the sole signal — a label always accompanies it). */
export function Dot({ color, className = '' }: { color: string; className?: string }): JSX.Element {
  return <span aria-hidden="true" className={`inline-block shrink-0 rounded-full ${className}`} style={{ width: 7, height: 7, background: color }} />
}

/** All lifecycle statuses (album + track), each with an AA-safe fg/bg/dot triad. */
export const STATUS_TONE: Record<string, { bg: string; fg: string; dot: string; label: string }> = {
  draft: { bg: '#f0ece3', fg: 'var(--as-ink-2)', dot: '#b9ae99', label: 'Draft' },
  mixed: { bg: 'var(--as-amber-tint)', fg: 'var(--as-amber)', dot: '#c79b3c', label: 'Mixed' },
  mastered: { bg: 'var(--as-blue-tint)', fg: 'var(--as-blue)', dot: '#5c7fa6', label: 'Mastered' },
  published: { bg: 'var(--as-sage-tint)', fg: 'var(--as-sage-2)', dot: 'var(--as-sage)', label: 'Published' },
  released: { bg: 'var(--as-gold-tint)', fg: 'var(--as-gold-600)', dot: 'var(--as-gold)', label: 'Released' },
}

/**
 * A lifecycle status pill. Colour never carries meaning alone — the label text
 * is always present (WCAG 1.4.1). Falls back to the Draft tone for unknown keys.
 *
 * @param status - Album/track status key.
 * @param size - `sm` for dense rows.
 */
export function StatusPill({ status, size = 'md', className = '' }: { status: string; size?: BtnSize; className?: string }): JSX.Element {
  const tone = STATUS_TONE[status] ?? STATUS_TONE.draft
  const sm = size === 'sm'
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full font-semibold ${sm ? 'px-2 py-0.5 text-[10.5px]' : 'px-2.5 py-[3px] text-[11.5px]'} ${className}`}
      style={{ background: tone.bg, color: tone.fg }}
    >
      <Dot color={tone.dot} className={sm ? '!h-[5px] !w-[5px]' : '!h-[6px] !w-[6px]'} />
      {tone.label}
    </span>
  )
}

type PillTone = 'neutral' | 'gold' | 'sage' | 'rose' | 'blue'
const PILL_TONE: Record<PillTone, CSSProperties> = {
  neutral: { background: '#f3efe7', color: 'var(--as-ink-2)' },
  gold: { background: 'var(--as-gold-tint)', color: 'var(--as-gold-600)' },
  sage: { background: 'var(--as-sage-tint)', color: 'var(--as-sage-2)' },
  rose: { background: 'var(--as-rose-tint)', color: 'var(--as-rose)' },
  blue: { background: 'var(--as-blue-tint)', color: 'var(--as-blue)' },
}

/** A small toned chip for counts / meta (distinct from the lifecycle StatusPill). */
export function Pill({ tone = 'neutral', children, className = '' }: { tone?: PillTone; children: ReactNode; className?: string }): JSX.Element {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-[3px] text-[11.5px] font-semibold ${className}`} style={PILL_TONE[tone]}>
      {children}
    </span>
  )
}

/**
 * Album cover: the real artwork when present and loadable, otherwise a
 * deterministic gradient placeholder (stable per album) with a small disc mark.
 * Decorative — the album title always labels it elsewhere. Delegates to the
 * shared {@link CoverImage}, which owns the `onError → gradient` fallback so a
 * dead/404 URL degrades to the placeholder instead of broken alt text.
 *
 * @param artworkUrl - The album's artwork URL, if any.
 * @param seed - Stable seed for the placeholder gradient (album id or title).
 * @param alt - Accessible name for the real artwork image.
 * @param size - Square edge length in px.
 * @param radius - Corner radius in px.
 * @returns The album cover image or its seeded placeholder.
 */
export function Cover({
  artworkUrl,
  seed,
  alt,
  size = 48,
  radius = 10,
  className = '',
}: {
  artworkUrl?: string | null
  seed: string
  alt: string
  size?: number
  radius?: number
  className?: string
}): JSX.Element {
  return <CoverImage url={artworkUrl} seed={seed} alt={alt} size={size} radius={radius} className={className} />
}

/** A shimmering skeleton block for loading states. */
export function Skel({ w, h = 12, r = 8, className = '' }: { w?: number | string; h?: number; r?: number; className?: string }): JSX.Element {
  return (
    <span
      aria-hidden="true"
      className={`block animate-pulse bg-[var(--as-line-2)] ${className}`}
      style={{ width: w, height: h, borderRadius: r }}
    />
  )
}

/**
 * A compact readiness bar (0–100%) with a monospace percentage. Turns sage at
 * 100%, gold below. The percentage text is the accessible value.
 *
 * @param pct - Completion percentage 0–100.
 */
export function MiniBar({ pct }: { pct: number }): JSX.Element {
  const clamped = Math.max(0, Math.min(100, Math.round(pct)))
  return (
    <div className="flex min-w-[130px] items-center gap-[9px]">
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[#e7e0d2]">
        <div
          className="h-full rounded-full transition-[width] duration-500"
          style={{ width: `${clamped}%`, background: clamped >= 100 ? 'var(--as-sage)' : 'var(--as-gold)' }}
        />
      </div>
      <span className="w-[34px] text-right font-[family-name:var(--as-font-mono)] text-[11.5px] font-semibold text-[var(--as-ink-2)]">{clamped}%</span>
    </div>
  )
}

/** `m:ss` from a duration in seconds (`--:--` when undefined). */
export function formatDuration(seconds: number | undefined | null): string {
  if (seconds === undefined || seconds === null) return '--:--'
  const s = Math.max(0, Math.floor(seconds))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/** A short human date (`Mar 20, 2026`) from a `YYYY-MM-DD` string, or null. */
export function formatDate(date: string | undefined | null): string | null {
  if (!date) return null
  const parsed = new Date(`${date}T00:00:00`)
  if (Number.isNaN(parsed.getTime())) return date
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}
